import {
    Box, Divider, Flex, Grid, IconButton, Image, Text, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, FormControl, FormLabel, Input, Button, FormErrorMessage,
    useDisclosure,
    Checkbox,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, Badge, Spinner, Center,
} from "@chakra-ui/react";
import { EmployeePersonalInfo, HeirarchyCheck } from './modal';
import { Controller, useForm } from "react-hook-form";
import { useFetch } from '../../../../../hooks/useFetch';
import { CustomToast } from "../../../../../components";
import { yupResolver } from "@hookform/resolvers/yup";
import EditLogo from "../../../../../assets/images/EditLogo.svg";
import IdentityDetailLogo from "../../../../../assets/images/IdentityDetailLogo.svg";
import * as yup from "yup";
import { useAuth } from "../../../../../hooks/useAuth";
import { useState } from "react";
import { RepeatClockIcon } from "@chakra-ui/icons";
import { showDate } from "../../../../../utilities/helper";

interface Props {
    employeeId: number | null;
    personalDetail?: EmployeePersonalInfo;
    reload: () => void;
};

export interface IHierarchyCheck {
    empId?: number | null;
    teamLead: boolean;
    manager: boolean;
};

interface IHierarchyHistoryRow {
    role: string;
    leader_Id: number;
    leader_Name: string;
    joining_Date: string;
    leaving_Date: string | null;
    current: boolean;
}

const HierarchyCheck = ({ employeeId, personalDetail, reload }: Props) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isHistoryOpen,
        onOpen: onHistoryOpen,
        onClose: onHistoryClose,
    } = useDisclosure();

    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const { hasPermission } = useAuth();

    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyData, setHistoryData] = useState<IHierarchyHistoryRow[]>([]);

    const HierarchySchema: any = yup.object().shape({
        teamLead: yup.boolean(),
        manager: yup.boolean(),
    })

    const { control, handleSubmit, register, formState: { errors, isSubmitting }, reset } = useForm<IHierarchyCheck>({
        resolver: yupResolver(HierarchySchema),
        defaultValues: {
            teamLead: false,
            manager: false,
        },
    });

    function handleEditClick() {
        reset({
            manager: personalDetail?.isManager,
            teamLead: personalDetail?.isTeamLead,
        });
        onOpen();
    };

    const onSubmit = async (data: IHierarchyCheck) => {
        data.empId = employeeId;

        let endpoint = `Hrlense_Employee/UpdateHierarchy/${employeeId}`;
        const response = await fetchApi(endpoint, "PUT", data, null, "Hierarchy Details Updated");
        if (response) {
            reload();
            onClose();
        }
    };

    async function handleHistoryClick() {
        if (!employeeId) return;

        onHistoryOpen();
        setHistoryLoading(true);

        const endpoint = `Hrlense_Employee/employeeHierarchyHistory/${employeeId}`;
        const response = await fetchApi(endpoint, "GET");

        if (response) {
            setHistoryData(response?.history ?? []);
        }
        setHistoryLoading(false);
    };

    return (
        <>
            <Box pt={2} mb={6} className="main">
                <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
                    <Flex align="center" gap={3}>
                        <Image src={IdentityDetailLogo} alt={"Benifits & ID Numberss"} />
                        <Text className="font-poppins text_sm text_medium col">
                            Hierarchy Check
                        </Text>
                    </Flex>

                    <Flex align="center" gap={2}>
                        {hasPermission('Employee', 'ViewHierarchyHistory') && (
                            <IconButton
                                aria-label="History"
                                icon={<RepeatClockIcon color="#006838" />}
                                variant="none"
                                onClick={handleHistoryClick}
                            />
                        )}

                        {hasPermission('Employee', 'UpdateHirearchy') && (
                            <IconButton
                                aria-label="Edit"
                                icon={<Image src={EditLogo} />}
                                variant="none"
                                onClick={() => handleEditClick()}
                            />
                        )}
                    </Flex>
                </Flex>

                <Box py={2} px={10} bg="#FFFFFF" borderRadius={8}>
                    {HeirarchyCheck.map((data, index) => {
                        return (
                            <Grid key={index} templateColumns="1fr 1fr" alignItems="center" mb={3}>
                                <Text
                                    className="font-poppins text_sm text_medium"
                                    color="rgba(102, 102, 102, 1)"
                                >
                                    {data?.label}
                                </Text>
                                <Text className="font-poppins text_sm text_medium col">
                                    {personalDetail?.[data?.key] ? "Yes" : "No"}
                                </Text>
                            </Grid>
                        )
                    })}
                </Box>
            </Box>

            {/* Edit Hierarchy Drawer */}
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>Update Hierarchy </DrawerHeader>
                    <DrawerBody>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <FormControl isInvalid={!!errors.manager} mb={4}>
                                <Flex align="center" justify="space-between" pr={10}>
                                    <FormLabel> Manager </FormLabel>
                                    <Controller
                                        name="manager" control={control}
                                        render={({ field }) => (
                                            <Checkbox isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                                        )}
                                    />
                                </Flex>
                                <FormErrorMessage>{errors.manager?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={!!errors.teamLead} mb={4} pr={10}>
                                <Flex align="center" justify="space-between">
                                    <FormLabel mb={0}>Team Lead</FormLabel>
                                    <Controller
                                        name="teamLead"
                                        control={control}
                                        render={({ field }) => (
                                            <Checkbox isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                                        )}
                                    />
                                </Flex>
                                <FormErrorMessage>{errors.teamLead?.message}</FormErrorMessage>
                            </FormControl>

                            <Box mt={6} textAlign="right">
                                <Button type="submit" className="btn_theme btn_theme:hover" isLoading={isSubmitting}>
                                    Update
                                </Button>
                            </Box>
                        </form>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Hierarchy History Drawer */}
            <Drawer isOpen={isHistoryOpen} placement="right" onClose={onHistoryClose} size="lg">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>Hierarchy History</DrawerHeader>
                    <DrawerBody>
                        {historyLoading ? (
                            <Center py={10}>
                                <Spinner color="#006838" />
                            </Center>
                        ) : historyData.length === 0 ? (
                            <Center py={10}>
                                <Text color="gray.500">No hierarchy history found.</Text>
                            </Center>
                        ) : (
                            <TableContainer>
                                <Table size="sm" variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>Role</Th>
                                            <Th>Leader</Th>
                                            <Th>Joined</Th>
                                            <Th>Left</Th>
                                            <Th>Status</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {historyData.map((row, idx) => (
                                            <Tr key={idx}>
                                                <Td>{row.role}</Td>
                                                <Td>{row.leader_Name}</Td>
                                                <Td>{row.joining_Date ? showDate(row.joining_Date) : "-"}</Td>
                                                <Td>{row.leaving_Date ? showDate(row.leaving_Date) : "-"}</Td>
                                                <Td>
                                                    <Badge colorScheme={row.current ? "green" : "gray"}>
                                                        {row.current ? "Active" : "Ended"}
                                                    </Badge>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}

export default HierarchyCheck